import { Analytics } from '@porsche-code/kado-analytics-client';
import { getIntendedPath, useAuth } from '@porsche-code/kado-auth';
import { Rum } from '@porsche-code/kado-rum-client';
import { ErrorBoundary } from '@porsche-code/kado-ui';
import { PToast } from '@porsche-design-system/components-react';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';

import config from '@/config/amplifyconfiguration';
import cookieStorageData from '@/config/cookieStorage';
import rumConfig from '@/config/rum';
import {
  PersonActionKind,
  useAbilityContext,
  usePersonContext,
} from '@/context';

import { version } from '../package.json';
import { Migrations } from './migrations';
import { routeTree } from './routeTree.gen';

const router = createRouter({ routeTree, scrollRestoration: () => false });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

Amplify.configure(config);
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage(cookieStorageData),
);

Analytics.configure({
  appName: 'dashboard',
  appVersion: version,
});

Rum.configure({
  ...rumConfig,
  appVersion: version,
});

const App = () => {
  const { dispatch: dispatchPerson } = usePersonContext();
  const { state: ability } = useAbilityContext();

  useAuth(
    (user) => {
      dispatchPerson({ type: PersonActionKind.Authenticate, payload: user });

      // After successful authentication, redirect to intended path if stored
      const intendedPath = getIntendedPath();
      if (intendedPath && intendedPath !== window.location.pathname) {
        window.location.href = intendedPath;
      }
    },
    () => {
      dispatchPerson({ type: PersonActionKind.Reset });
      ability.update([]);
    },
  );

  return (
    <ErrorBoundary name="dashboard">
      <Migrations>
        <RouterProvider router={router} />
        <PToast />
      </Migrations>
    </ErrorBoundary>
  );
};

export default App;
